/**
 * Settings API
 * Infrastructure Layer - External API communication
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import { unwrapApiData } from './unwrapApiData';
import {
  SettingsDTO,
  UpdateSettingsDTO,
  ContentSettingDTO,
  ISaveFilterApiDto,
  IFilterSettingsApiDto,
} from '../../application/dto/Settings.dto';

/**
 * Settings API (RTK Query)
 * Infrastructure layer - handles HTTP communication
 */
export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Settings', 'FilterSettings', 'ContentSetting'],
  endpoints: build => ({
    getSettings: build.query<SettingsDTO, string>({
      query: userId => ({
        url: `/settings/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<SettingsDTO>(response),
      providesTags: ['Settings'],
    }),

    updateSettings: build.mutation<
      SettingsDTO,
      { userId: string; settings: UpdateSettingsDTO }
    >({
      query: ({ userId, settings }) => ({
        url: `/settings/${userId}`,
        method: 'PUT',
        body: settings,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<SettingsDTO>(response),
      invalidatesTags: ['Settings'],
    }),

    getContentSetting: build.query<ContentSettingDTO, string>({
      query: key => ({
        url: `/system/owner/settings/${key}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<ContentSettingDTO>(response),
      providesTags: (_result, _error, key) => [
        { type: 'ContentSetting', id: key },
      ],
    }),

    upsertContentSetting: build.mutation<
      ContentSettingDTO,
      { key: string; body: Record<string, unknown> }
    >({
      query: ({ key, body }) => ({
        url: `/system/owner/settings/${key}`,
        method: 'PUT',
        body: { body },
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<ContentSettingDTO>(response),
      async onQueryStarted({ key }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            settingsApi.util.updateQueryData(
              'getContentSetting',
              key,
              () => data,
            ),
          );
        } catch {
          // keep cached setting when save fails
        }
      },
    }),

    // Filter endpoints
    getFilter: build.query<IFilterSettingsApiDto | null, string>({
      query: filterId => ({
        url: `settings/filter/${filterId}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<IFilterSettingsApiDto | null>(response),
      providesTags: (result, error, filterId) => [
        { type: 'FilterSettings', id: filterId },
      ],
    }),

    saveFilter: build.mutation<IFilterSettingsApiDto, ISaveFilterApiDto>({
      query: dto => ({
        url: 'settings/filter/save',
        method: 'POST',
        body: dto,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<IFilterSettingsApiDto>(response),
      invalidatesTags: (result, error, dto) => [
        { type: 'FilterSettings', id: dto.filterId },
      ],
    }),

    deleteFilter: build.mutation<boolean, string>({
      query: filterId => ({
        url: `settings/filter/${filterId}/reset`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, filterId) => [
        { type: 'FilterSettings', id: filterId },
      ],
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetContentSettingQuery,
  useUpsertContentSettingMutation,
  useGetFilterQuery,
  useLazyGetFilterQuery,
  useSaveFilterMutation,
  useDeleteFilterMutation,
} = settingsApi;
